import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Calculator, Activity, User, Ruler, Weight } from 'lucide-react';

const Index = () => {
  const [geschlecht, setGeschlecht] = useState('');
  const [alter, setAlter] = useState('');
  const [groesse, setGroesse] = useState('');
  const [gewicht, setGewicht] = useState('');
  const [aktivitaet, setAktivitaet] = useState('');
  const [grundumsatz, setGrundumsatz] = useState(0);
  const [gesamtumsatz, setGesamtumsatz] = useState(0);
  const [fehler, setFehler] = useState('');

  const aktivitaetsfaktoren = [
    { value: '1.2', label: 'Kaum Bewegung / Bettlägerig (1.2)' },
    { value: '1.35', label: 'Überwiegend sitzend (1.3-1.4)' },
    { value: '1.55', label: 'Sitzend + etwas aktiv (1.5-1.6)' },
    { value: '1.75', label: 'Überwiegend stehend/gehend (1.7-1.8)' },
    { value: '2.05', label: 'Körperlich schwere Arbeit / intensiver Sport (1.9-2.2)' }
  ];

  const validateInputs = () => {
    const alterNum = parseFloat(alter);
    const groesseNum = parseFloat(groesse);
    const gewichtNum = parseFloat(gewicht);

    if (!geschlecht || !alter || !groesse || !gewicht || !aktivitaet) {
      setFehler('Bitte füllen Sie alle Felder aus.');
      return false;
    }

    if (alterNum < 1 || alterNum > 120) {
      setFehler('Alter muss zwischen 1 und 120 Jahren liegen.');
      return false;
    }

    if (groesseNum < 50 || groesseNum > 300) {
      setFehler('Körpergröße muss zwischen 50 und 300 cm liegen.');
      return false;
    }

    if (gewichtNum < 10 || gewichtNum > 500) {
      setFehler('Körpergewicht muss zwischen 10 und 500 kg liegen.');
      return false;
    }

    setFehler('');
    return true;
  };

  const berechneGrundumsatz = () => {
    const alterNum = parseFloat(alter);
    const groesseNum = parseFloat(groesse);
    const gewichtNum = parseFloat(gewicht);

    let gu = 0;

    if (geschlecht === 'maennlich') {
      // Männer: GU = (10 × Gewicht) + (6.25 × Größe) − (5 × Alter) + 5
      gu = (10 * gewichtNum) + (6.25 * groesseNum) - (5 * alterNum) + 5;
    } else if (geschlecht === 'weiblich') {
      // Frauen: GU = (10 × Gewicht) + (6.25 × Größe) − (5 × Alter) − 161
      gu = (10 * gewichtNum) + (6.25 * groesseNum) - (5 * alterNum) - 161;
    } else if (geschlecht === 'divers') {
      // Mittelwert aus männlicher und weiblicher Formel
      const guMann = (10 * gewichtNum) + (6.25 * groesseNum) - (5 * alterNum) + 5;
      const guFrau = (10 * gewichtNum) + (6.25 * groesseNum) - (5 * alterNum) - 161;
      gu = (guMann + guFrau) / 2;
    }

    return Math.round(gu);
  };

  const berechneGesamtumsatz = (gu: number) => {
    const faktor = parseFloat(aktivitaet);
    return Math.round(gu * faktor);
  };

  const handleBerechnung = () => {
    if (validateInputs()) {
      const gu = berechneGrundumsatz();
      const gu_gesamt = berechneGesamtumsatz(gu);
      setGrundumsatz(gu);
      setGesamtumsatz(gu_gesamt);
    }
  };

  // Live-Berechnung bei Eingabeänderungen
  useEffect(() => {
    if (geschlecht && alter && groesse && gewicht && aktivitaet && !fehler) {
      if (validateInputs()) {
        const gu = berechneGrundumsatz();
        const gu_gesamt = berechneGesamtumsatz(gu);
        setGrundumsatz(gu);
        setGesamtumsatz(gu_gesamt);
      }
    }
  }, [geschlecht, alter, groesse, gewicht, aktivitaet]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-3">
            <Calculator className="h-10 w-10 text-blue-600" />
            Kalorienbedarf-Rechner
          </h1>
          <p className="text-lg text-gray-600">
            Berechnung von Grundumsatz und Gesamtenergiebedarf nach der Mifflin-St.-Jeor-Formel
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Eingabebereich */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Ihre Daten
              </CardTitle>
              <CardDescription>
                Geben Sie Ihre persönlichen Daten für die Berechnung ein
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="geschlecht">Geschlecht</Label>
                <Select value={geschlecht} onValueChange={setGeschlecht}>
                  <SelectTrigger>
                    <SelectValue placeholder="Geschlecht auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="maennlich">Männlich</SelectItem>
                    <SelectItem value="weiblich">Weiblich</SelectItem>
                    <SelectItem value="divers">Divers</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="alter">Alter (Jahre)</Label>
                <Input
                  id="alter"
                  type="number"
                  placeholder="z.B. 30"
                  value={alter}
                  onChange={(e) => setAlter(e.target.value)}
                  min="1"
                  max="120"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="groesse" className="flex items-center gap-2">
                  <Ruler className="h-4 w-4" />
                  Körpergröße (cm)
                </Label>
                <Input
                  id="groesse"
                  type="number"
                  placeholder="z.B. 175"
                  value={groesse}
                  onChange={(e) => setGroesse(e.target.value)}
                  min="50"
                  max="300"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="gewicht" className="flex items-center gap-2">
                  <Weight className="h-4 w-4" />
                  Körpergewicht (kg)
                </Label>
                <Input
                  id="gewicht"
                  type="number"
                  placeholder="z.B. 70"
                  value={gewicht}
                  onChange={(e) => setGewicht(e.target.value)}
                  min="10"
                  max="500"
                  step="0.1"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="aktivitaet" className="flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Aktivitätsgrad
                </Label>
                <Select value={aktivitaet} onValueChange={setAktivitaet}>
                  <SelectTrigger>
                    <SelectValue placeholder="Aktivitätsgrad auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {aktivitaetsfaktoren.map((faktor) => (
                      <SelectItem key={faktor.value} value={faktor.value}>
                        {faktor.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button 
                onClick={handleBerechnung} 
                className="w-full" 
                size="lg"
                disabled={!geschlecht || !alter || !groesse || !gewicht || !aktivitaet}
              >
                <Calculator className="h-4 w-4 mr-2" />
                Berechnen
              </Button>

              {fehler && (
                <Alert variant="destructive">
                  <AlertDescription>{fehler}</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Ergebnisbereich */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Ihre Ergebnisse
              </CardTitle>
              <CardDescription>
                Berechneter Kalorienbedarf basierend auf Ihren Angaben
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {grundumsatz > 0 ? (
                <>
                  <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">
                      Grundumsatz (GU)
                    </h3>
                    <p className="text-3xl font-bold text-blue-700">
                      {grundumsatz.toLocaleString()} kcal/Tag
                    </p>
                    <p className="text-sm text-blue-600 mt-2">
                      Energiebedarf in völliger Ruhe
                    </p>
                  </div>

                  <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                    <h3 className="text-lg font-semibold text-green-900 mb-2">
                      Gesamtenergiebedarf (inkl. Aktivität)
                    </h3>
                    <p className="text-3xl font-bold text-green-700">
                      {gesamtumsatz.toLocaleString()} kcal/Tag
                    </p>
                    <p className="text-sm text-green-600 mt-2">
                      Täglicher Kalorienbedarf mit Aktivität
                    </p>
                  </div>

                  <Alert>
                    <AlertDescription className="text-sm">
                      <strong>Hinweis:</strong> Die Berechnung liefert Richtwerte und ersetzt keine individuelle ernährungs- oder ärztliche Beratung. Bei besonderen gesundheitlichen Bedingungen konsultieren Sie bitte einen Fachmann.
                    </AlertDescription>
                  </Alert>

                  {geschlecht === 'divers' && (
                    <Alert>
                      <AlertDescription className="text-sm">
                        <strong>Info:</strong> Für "divers" wurde der Mittelwert aus männlicher und weiblicher Formel verwendet.
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Calculator className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Füllen Sie alle Felder aus,</p>
                  <p>um Ihren Kalorienbedarf zu berechnen.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Informationsbereich */}
        <Card className="mt-8 shadow-lg">
          <CardHeader>
            <CardTitle>Über die Mifflin-St.-Jeor-Formel</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-2">Grundumsatz-Berechnung:</h4>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li><strong>Männer:</strong> (10 × Gewicht) + (6.25 × Größe) − (5 × Alter) + 5</li>
                  <li><strong>Frauen:</strong> (10 × Gewicht) + (6.25 × Größe) − (5 × Alter) − 161</li>
                  <li><strong>Divers:</strong> Mittelwert aus beiden Formeln</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Aktivitätsfaktoren:</h4>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li><strong>1.2:</strong> Kaum Bewegung, bettlägerig</li>
                  <li><strong>1.3-1.4:</strong> Überwiegend sitzende Tätigkeit</li>
                  <li><strong>1.5-1.6:</strong> Sitzend + leichte Aktivität</li>
                  <li><strong>1.7-1.8:</strong> Überwiegend stehend/gehend</li>
                  <li><strong>1.9-2.2:</strong> Schwere körperliche Arbeit/Sport</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Index;
